import { 
  Annotation, 
  Data,
  AlertZone,
  DataPoint,
} from "./dashboard.types";
import tinycolor from "tinycolor2";
import moment, { Moment } from "moment";

function createLabels(start: Moment, end: Moment) {
  const startDate = moment(start);
  const endDate = moment(end);
  const labels: string[][] = [];
  while (startDate.diff(endDate, 'days') <= 0) {
    const date = startDate.format('MM/DD');
    const day = startDate.format('dd');
    labels.push([date, day]);
    startDate.add(1, 'days')
  }
  return labels;
}

function createChartOptions(
  startDate: Moment, 
  noOfDays: number, 
  end: number, 
  step: number, 
  alertZones: AlertZone[],
  assessmentType: string,
  context: any
) {
  const annotations = createAnnotations(noOfDays, end, startDate, alertZones);
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      datalabels: {
        display: false
      },
      propagate: true,
      legend: {
        display: false
      },
      tooltip: {
        enabled: false
      },
      annotation: {
        common: {
          drawTime: 'beforeDraw'
        },
        annotations: annotations
      }
    },
    scales: {
      y: {
        max: end,
        min: 0,
        ticks: {
          stepSize: step,
          font: function(context: any) {
            const width = context.chart.width;
            const size = width / 160;
             return {
               size: size
            };
          },
        }
      },
      x: {
        ticks: {
          labelOffset: 20,
          font: function(context: any) {
            const width = context.chart.width;
            const size = width / 160;
             return {
               size: size
            };
          },
          callback: function(value: any, index: any, values: any) {
            return index == values.length - 1 ? '' : (this as any).getLabelForValue(value);
          }
        }
      }
    },
    onClick: (event: any) => handlePointClick(
      event, 
      context, 
      assessmentType,
      alertZones
    ),
    onHover: (event: any, chartElement: any) => {
      const target = event.native ? event.native.target : event.target;
      if (chartElement.length) {
        target.style.cursor = 'pointer';
      } else {
        target.style.cursor = 'default';
      }
    }
  }
}

function handlePointClick(
  event: any, 
  context: any, 
  assessmentType: string,
  alertZones: AlertZone[]
) {
  const chart = event.chart; // Assuming the event object contains a reference to the chart

  // Get the points at the clicked position
  const points = chart.getElementsAtEventForMode(
    event.native, // Pass the native event object
    'nearest',
    { intersect: true },
    true
  );

  if (points.length) {
    const firstPoint = points[0];
    const value = chart.data.datasets[firstPoint.datasetIndex].data[firstPoint.index];

    context.assessmentResultShow = true;
    context.assessmentType = assessmentType;
    context.assessmentIds = value.assessmentIds;
    context.assessmentDates = value.assessmentDates;
    context.assessmentValues = value.assessmentValues;
    context.isAssessment = value.isAssessment;
    context.alertZones = alertZones;
  }
}

function createChartData(label: string[][], data: Data[], dataPoints: DataPoint[]) {
  return {
    labels: label,
    datasets: [
      {
        pointBackgroundColor: function(value: any) {
          if (value.parsed.y && value.parsed.x) {
            const result = dataPoints.find(item => item.y == value.parsed.y && item.x == value.parsed.x);
            if (result?.multiple) {
              return "white";
            }
          }
          return "black";
        },
        borderColor: "black",
        parsing: false,
        borderWidth: 1,
        pointRadius: function (context: any) : number {
          const width = context.chart.width;
          const size = width / 350;
          return size;
        },
        datalabels: {
          clip: false,
          display: true,
          align: 'top',
          font: function(context: any) {
            const width = context.chart.width;
            const size = width / 120;
             return {
               size: size,
               weight: 'bold'
            };
          },
          formatter: (value: any) => (value.y)
        },
        data: data,
        spanGaps: true
      }
    ]
  }
}

function createAnnotations(noOfDays: number, end: number, startDate: Moment, alertZones: AlertZone[]) {
  const annotations: Annotation[] = [];
  getWeekendBoxes(annotations, end, noOfDays, startDate);
  getHorizontalItems(annotations, noOfDays, end, alertZones);
  return annotations;
}

function getHorizontalItems(annotations: Annotation[], noOfDays: number, end: number, alertZones: AlertZone[]) {
  for (let i = 0; i < end ; i = i + 1) {
    const color = getColor(i, alertZones);
    annotations.push({
      id: 'horizontal-box' + i,
      type: 'box',
      xMin: -0.5,
      xMax: noOfDays,
      yMin: i,
      yMax: i + 2,
      backgroundColor: color,
      borderWidth: 0
    });
  }
}

function getWeekendBoxes(annotations: Annotation[], end: number, noOfDays: number, startDate: Moment) {
  const dayOfWeek = startDate.day();
  if (dayOfWeek == 0) {
    annotations.push({
      id: 'weekend' + 0,
      type: 'box',
      xMin: 0,
      xMax: 1,
      yMin: 0,
      yMax: end,
      borderWidth: 0,
      backgroundColor: "rgba(154,154,154,0.40)",
      z: 2
    });
  }
  const weekendStart = 6 - dayOfWeek;
  for (let i = weekendStart; i < noOfDays; i = i + 7) {
    annotations.push({
      id: 'weekend' + i,
      type: 'box',
      xMin: i,
      xMax: i + 2,
      yMin: 0,
      yMax: end,
      borderWidth: 0,
      backgroundColor: "rgba(154,154,154,0.40)",
      z: 2
    });
  }
}

function getColor(index: number, alertZones: AlertZone[], darker = false, inclusive = false) {
  const alertZone = alertZones.filter(e => inclusive ? e.startZone <= index && e.endZone >= index :
    e.startZone <= index && e.endZone > index);
  if (alertZone.length > 0) {
    return darker ? tinycolor(alertZone[0].color).darken(25).toString() : alertZone[0].color;
  }
  return "#FFFFFF";
}

function setChartOptions(
  startDate: Moment, 
  labelsLength: number, 
  phq9AlertZones: AlertZone[], 
  gad7AlertZones: AlertZone[],
  sowsAlertZones: AlertZone[],
  cravingsAlertZones: AlertZone[],
  context: any,
  store: any
) {
  const phq9ChartOptions = createChartOptions(
    startDate, 
    labelsLength, 
    27, 
    3, 
    phq9AlertZones,
    'PHQ-9',
    context
  );
  store.commit("Dashboard/updateField", {
    path: "phq9ChartOptions",
    value: phq9ChartOptions
  });
  const gad7ChartOptions = createChartOptions(
    startDate, 
    labelsLength, 
    21, 
    3, 
    gad7AlertZones, 
    'GAD-7',
    context
  );
  store.commit("Dashboard/updateField", {
    path: "gad7ChartOptions",
    value: gad7ChartOptions
  });
  const sowsChartOptions = createChartOptions(
    startDate, 
    labelsLength, 
    64, 
    8, 
    sowsAlertZones, 
    'SOWS',
    context
  );
  store.commit("Dashboard/updateField", {
    path: "sowsChartOptions",
    value: sowsChartOptions
  });
  const cravingsChartOptions = createChartOptions(
    startDate, 
    labelsLength, 
    6, 
    1, 
    cravingsAlertZones, 
    'Cravings',
    context
  );
  store.commit("Dashboard/updateField", {
    path: "cravingsChartOptions",
    value: cravingsChartOptions
  });
}

export { createLabels, createChartOptions, createChartData, setChartOptions, getColor };